import { type PointerEvent, useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Monitor, Repeat, RotateCcw, Settings, Volume2, VolumeX, X } from "lucide-react";
import { hasWebGL, isFullscreen, isTypingTarget, onFullscreenChange, toggleFullscreen } from "../bingo-shared/display";
import { cancelSpeech } from "../bingo-shared/speech";
import { hushHall, playCallDing, playVictory, resumeHallAudio, speakCall, unlockHallAudio } from "./audio";
import { fireHallBingoConfetti, HallBoard } from "./HallBoard";
import { BoardGrid } from "./BoardGrid";
import { HallAtmosphere } from "./HallAtmosphere";
import { callLabel, columnFor } from "./logic";
import { connectHallOperator, openStageWindow, requestStageClose, STAGE_HELLO, STAGE_PATH, STAGE_WINDOW } from "./sync";
import { useHallStore } from "./store";
import { setHallRole } from "./role";
import { HALL_THEMES } from "./themes";
import { resetPhaseClock, tickPhase } from "./timeline";

export default function CallerV2() {
  setHallRole("operator");
  const rootRef = useRef<HTMLDivElement>(null);
  const stageWin = useRef<Window | null>(null);
  const ignoreStageHello = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [webgl, setWebgl] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [sceneFailed, setSceneFailed] = useState(false);
  const [stageLive, setStageLive] = useState(false);
  const [wantSplit, setWantSplit] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const hydrate = useHallStore((s) => s.hydrate);
  const phase = useHallStore((s) => s.phase);
  const current = useHallStore((s) => s.current);
  const called = useHallStore((s) => s.called);
  const remaining = useHallStore((s) => s.remaining);
  const draw = useHallStore((s) => s.draw);
  const skipToCall = useHallStore((s) => s.skipToCall);
  const replayLast = useHallStore((s) => s.replayLast);
  const celebrate = useHallStore((s) => s.celebrate);
  const endCelebrate = useHallStore((s) => s.endCelebrate);
  const reset = useHallStore((s) => s.reset);
  const soundEnabled = useHallStore((s) => s.soundEnabled);
  const voiceEnabled = useHallStore((s) => s.voiceEnabled);
  const voiceRepeats = useHallStore((s) => s.voiceRepeats);
  const voicePause = useHallStore((s) => s.voicePause);
  const quickCall = useHallStore((s) => s.quickCall);
  const tapSkip = useHallStore((s) => s.tapSkip);
  const mixSeconds = useHallStore((s) => s.mixSeconds);
  const theme = useHallStore((s) => s.theme);
  const setSoundEnabled = useHallStore((s) => s.setSoundEnabled);
  const setVoiceEnabled = useHallStore((s) => s.setVoiceEnabled);
  const setVoiceRepeats = useHallStore((s) => s.setVoiceRepeats);
  const setVoicePause = useHallStore((s) => s.setVoicePause);
  const setQuickCall = useHallStore((s) => s.setQuickCall);
  const setTapSkip = useHallStore((s) => s.setTapSkip);
  const setMixSeconds = useHallStore((s) => s.setMixSeconds);
  const setTheme = useHallStore((s) => s.setTheme);
  const liteMode = useHallStore((s) => s.liteMode);
  const displayLiteMode = useHallStore((s) => s.displayLiteMode);
  const setLiteMode = useHallStore((s) => s.setLiteMode);
  const setDisplayLiteMode = useHallStore((s) => s.setDisplayLiteMode);
  const setSharedLiteMode = useHallStore((s) => s.setSharedLiteMode);

  useEffect(() => {
    setMounted(true);
    setWebgl(hasWebGL());
    hydrate();
    return connectHallOperator();
  }, [hydrate]);

  useEffect(() => {
    const sync = () => setFullscreen(isFullscreen());
    return onFullscreenChange(sync);
  }, []);

  useEffect(() => {
    let lastHello = 0;
    const onHello = () => {
      if (ignoreStageHello.current) return;
      lastHello = Date.now();
      setStageLive(true);
    };
    window.addEventListener(STAGE_HELLO, onHello);
    const tick = window.setInterval(() => {
      if (stageWin.current?.closed) {
        lastHello = 0;
        setStageLive(false);
      }
      if (lastHello && Date.now() - lastHello > 5000) setStageLive(false);
    }, 400);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener(STAGE_HELLO, onHello);
    };
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") resumeHallAudio();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      hushHall();
    };
  }, []);

  useEffect(() => {
    resetPhaseClock(phase);
  }, [phase]);

  useEffect(() => {
    if (phase === "idle" || phase === "celebrating") return;
    let last = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      tickPhase(Math.min(0.1, (now - last) / 1000), phase);
      last = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    if (phase !== "calling" || current == null) return;
    if (voiceEnabled) speakCall(callLabel(current), voiceRepeats, voicePause);
  }, [phase, current, voiceRepeats, voiceEnabled, voicePause]);

  useEffect(() => {
    if (phase !== "celebrating") return;
    const t = window.setTimeout(() => endCelebrate(), 4200);
    return () => window.clearTimeout(t);
  }, [phase, endCelebrate]);

  const split = wantSplit || stageLive;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.code === "Space") {
        e.preventDefault();
        if (settingsOpen) return;
        unlockHallAudio();
        const hall = useHallStore.getState();
        if (hall.tapSkip && (hall.phase === "mixing" || hall.phase === "dropping")) hall.skipToCall();
        else hall.draw();
      } else if (e.key === "Escape") {
        setSettingsOpen(false);
        setConfirmReset(false);
        if (useHallStore.getState().phase === "celebrating") endCelebrate();
      } else if ((e.key === "f" || e.key === "F") && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (rootRef.current) void toggleFullscreen(rootRef.current);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [settingsOpen, endCelebrate]);

  const busy = phase === "mixing" || phase === "dropping" || phase === "calling" || phase === "seating";
  const empty = remaining.length === 0 && !busy;
  const sceneOn = mounted && webgl && !sceneFailed;
  const mixVisual = phase === "mixing" || phase === "dropping";
  const canTapSkip = tapSkip && mixVisual && !settingsOpen;
  const canTapDraw = !busy && !empty && !settingsOpen && phase !== "celebrating";

  const onDraw = () => {
    unlockHallAudio();
    if (empty) return;
    draw();
  };

  const onSurfacePointer = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const node = e.target;
    if (!(node instanceof Element)) return;
    if (node.closest("[data-no-draw]")) return;
    if (canTapSkip) {
      unlockHallAudio();
      skipToCall();
      return;
    }
    if (!canTapDraw) return;
    onDraw();
  };

  const onBingo = () => {
    if (phase === "celebrating") return;
    unlockHallAudio();
    celebrate();
    if (soundEnabled) playVictory();
    if (voiceEnabled) speakCall("Bingo!", 2);
    fireHallBingoConfetti(theme);
  };

  const replayNum =
    phase === "mixing" || phase === "dropping"
      ? (called.at(-1) ?? null)
      : current ?? called.at(-1) ?? null;

  const onReplay = () => {
    if (replayNum == null) return;
    unlockHallAudio();
    const shown = replayLast();
    if (!shown || phase === "calling") {
      if (soundEnabled) playCallDing();
    }
    speakCall(callLabel(replayNum), voiceRepeats, voicePause);
  };

  const onToggleProjector = () => {
    if (split) {
      ignoreStageHello.current = true;
      setWantSplit(false);
      setStageLive(false);
      setPopupBlocked(false);
      requestStageClose(stageWin.current);
      stageWin.current = null;
      return;
    }
    ignoreStageHello.current = false;
    setWantSplit(true);
    const win = openStageWindow();
    stageWin.current = win;
    setPopupBlocked(!win);
  };

  const letter = current != null ? columnFor(current) : null;
  const last = called.at(-1) ?? null;
  const lastLetter = last != null ? columnFor(last) : null;
  const status =
    phase === "mixing"
      ? tapSkip
        ? "Tap to show the number"
        : "Shuffling"
      : phase === "dropping"
        ? tapSkip
          ? "Tap to show the number"
          : "Ball out"
        : phase === "calling"
          ? "Now calling"
          : phase === "seating"
            ? "To the board"
            : empty
              ? "Board is full"
              : "Tap to draw";

  const chromeButtons = (
    <>
      <button type="button" data-no-draw onClick={onToggleProjector} className={`flex h-11 items-center justify-center gap-1.5 rounded-full border px-2.5 md:px-3 ${split ? "border-cream bg-cream text-ink" : "border-hall-line bg-hall-elevated text-cream"}`} aria-label={split ? "Close projector" : "Open projector window"} title={split ? "Close projector" : "Open projector window"}>
        <Monitor className="h-5 w-5" />
        <span className="hidden text-[0.7rem] font-medium md:inline">{split ? "Docked" : "Projector"}</span>
      </button>
      <button type="button" data-no-draw onClick={() => { if (rootRef.current) void toggleFullscreen(rootRef.current); }} className="flex h-11 w-11 items-center justify-center rounded-full border border-hall-line bg-hall-elevated" aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
        {fullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
      </button>
      {split ? null : (
        <button type="button" data-no-draw onClick={() => { setSettingsOpen(true); setConfirmReset(false); }} className="flex h-11 w-11 items-center justify-center rounded-full border border-hall-line bg-hall-elevated" aria-label="Settings">
          <Settings className="h-5 w-5" />
        </button>
      )}
    </>
  );

  const drawButtons = (
    <>
      <p className="col-span-2 text-center font-sans text-[0.65rem] leading-none text-cream-muted md:text-xs">{status}</p>
      <button type="button" data-no-draw onClick={() => { if (canTapSkip) { unlockHallAudio(); skipToCall(); return; } onDraw(); }} disabled={empty || (busy && !canTapSkip)} className="h-12 rounded-2xl bg-cream text-sm font-semibold text-ink transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-45 md:h-16 md:rounded-[1.75rem] md:text-lg">
        {canTapSkip ? "Show number" : busy ? "Calling…" : empty ? "All called" : "Draw next"}
      </button>
      <button type="button" data-no-draw onClick={onBingo} disabled={phase === "celebrating"} className="h-12 rounded-2xl border border-hall-line bg-hall-elevated text-sm font-medium text-cream transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-45 md:h-16 md:rounded-[1.75rem] md:text-base">Bingo</button>
      <button type="button" data-no-draw onClick={onReplay} disabled={replayNum == null} className="col-span-2 flex h-11 items-center justify-center gap-2 rounded-2xl border border-hall-line bg-hall-elevated text-sm font-medium text-cream transition-transform duration-150 ease-out active:scale-[0.96] disabled:opacity-45 md:h-12" aria-label={replayNum != null ? `Replay ${callLabel(replayNum)}` : "Replay last call"}>
        <Repeat className="h-4 w-4" />
        {replayNum != null ? `Replay ${columnFor(replayNum)}${replayNum}` : "Replay last"}
      </button>
    </>
  );

  const settingsFields = (
    <>
      <div className="mb-4">
        <p className="mb-2 text-sm font-medium tracking-wide text-cream-muted uppercase">Low power</p>
        <p className="mb-2 text-xs leading-snug text-cream-muted">Skips 3D, particles, and confetti. Voice and the board stay on.</p>
        {split ? (
          <>
            <label className="mb-2 flex h-11 items-center justify-between gap-4">
              <span className="text-sm">Control page</span>
              <input type="checkbox" checked={liteMode} onChange={(e) => setLiteMode(e.target.checked)} className="h-5 w-5 accent-cream" aria-label="Low power on the control page" />
            </label>
            <label className="flex h-11 items-center justify-between gap-4">
              <span className="text-sm">Display page</span>
              <input type="checkbox" checked={displayLiteMode} onChange={(e) => setDisplayLiteMode(e.target.checked)} className="h-5 w-5 accent-cream" aria-label="Low power on the display page" />
            </label>
          </>
        ) : (
          <label className="flex h-11 items-center justify-between gap-4">
            <span className="text-sm">This page</span>
            <input type="checkbox" checked={liteMode} onChange={(e) => setSharedLiteMode(e.target.checked)} className="h-5 w-5 accent-cream" aria-label="Low power" />
          </label>
        )}
      </div>
      <div className="mb-4">
        <p className="mb-2 text-sm font-medium tracking-wide text-cream-muted uppercase">Theme</p>
        <div className="grid grid-cols-3 gap-2">
          {HALL_THEMES.map((item) => {
            const active = theme === item.id;
            return (
              <button key={item.id} type="button" onClick={() => setTheme(item.id)} className={`flex h-14 flex-col items-center justify-center gap-1 rounded-2xl border text-sm font-medium ${active ? "border-cream bg-cream text-ink" : "border-hall-line bg-hall-elevated text-cream"}`} aria-pressed={active}>
                <span className="flex h-2 w-10 overflow-hidden rounded-full" aria-hidden>
                  <span className="h-full flex-1" style={{ background: item.swatch }} />
                  <span className="h-full flex-1" style={{ background: item.swatch2 }} />
                </span>
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
      <label className="mb-3 flex h-11 items-center justify-between gap-4">
        <span className="flex items-center gap-2 text-sm">{soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />} Sound effects</span>
        <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} className="h-5 w-5 accent-cream" />
      </label>
      <label className="mb-3 flex h-11 items-center justify-between gap-4">
        <span className="text-sm">Voice</span>
        <input type="checkbox" checked={voiceEnabled} onChange={(e) => setVoiceEnabled(e.target.checked)} className="h-5 w-5 accent-cream" />
      </label>
      <div className="mb-3">
        <div className="mb-2 flex items-center justify-between text-sm"><span>Voice repeats</span><span className="tabular-nums text-cream-muted">{voiceRepeats}</span></div>
        <input type="range" min={1} max={5} value={voiceRepeats} onChange={(e) => setVoiceRepeats(Number(e.target.value))} className="w-full accent-cream" />
      </div>
      <label className="mb-3 flex h-11 items-center justify-between gap-4">
        <span className="text-sm">Pause after letter</span>
        <input type="checkbox" checked={voicePause} onChange={(e) => setVoicePause(e.target.checked)} className="h-5 w-5 accent-cream" />
      </label>
      <label className="mb-3 flex h-11 items-center justify-between gap-4">
        <span className="text-sm">Quick call</span>
        <input type="checkbox" checked={quickCall} onChange={(e) => setQuickCall(e.target.checked)} className="h-5 w-5 accent-cream" />
      </label>
      <label className="mb-3 flex h-11 items-center justify-between gap-4">
        <span className="text-sm">Tap to skip shuffle</span>
        <input type="checkbox" checked={tapSkip} onChange={(e) => setTapSkip(e.target.checked)} className="h-5 w-5 accent-cream" />
      </label>
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between text-sm"><span>Shuffle length</span><span className="tabular-nums text-cream-muted">{mixSeconds.toFixed(1)}s</span></div>
        <input type="range" min={0.5} max={5} step={0.1} value={mixSeconds} onChange={(e) => setMixSeconds(Number(e.target.value))} className="w-full accent-cream" />
      </div>
      <button type="button" onClick={() => { if (!confirmReset) { setConfirmReset(true); return; } cancelSpeech(); reset(); setConfirmReset(false); setSettingsOpen(false); }} className={`mb-2 flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-semibold ${confirmReset ? "bg-red-600 text-white" : "border border-hall-line bg-hall-elevated text-cream"}`}>
        <RotateCcw className="h-4 w-4" />{confirmReset ? "Tap again to reset" : "Reset game"}
      </button>
    </>
  );

  return (
    <div
      ref={rootRef}
      className={`bingo-v2 relative flex h-dvh flex-col overflow-hidden text-cream touch-manipulation ${
        !split && (canTapDraw || canTapSkip) ? "cursor-pointer" : ""
      }`}
      data-phase={phase}
      data-called={called.length}
      data-theme={theme}
      data-mix={mixSeconds}
      onPointerUp={split ? undefined : onSurfacePointer}
    >
      <HallAtmosphere theme={theme} burst={phase === "celebrating"} />

      {split ? (
        <div className="v2-split relative z-[2] flex min-h-0 flex-1">
          <aside data-no-draw className="v2-split-controls relative z-30 flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-hall-line px-3 pt-[max(0.35rem,env(safe-area-inset-top))] pb-[max(0.45rem,env(safe-area-inset-bottom))] md:w-[min(26rem,42vw)] md:border-r md:px-5 md:pt-[max(0.75rem,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-sans text-[0.65rem] font-medium tracking-[0.28em] text-cream-muted uppercase">Caller</p>
                <h1 className="v2-title font-display text-[1.15rem] leading-none font-semibold tracking-tight md:text-2xl">Bingo Blast</h1>
              </div>
              {chromeButtons}
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-hall-line bg-hall-elevated px-3 py-2">
              <span className="text-[0.65rem] tracking-[0.18em] text-cream-muted uppercase">Called</span>
              <span className="font-display text-xl font-semibold tabular-nums">{called.length}<span className="text-sm text-cream-muted/60">/75</span></span>
            </div>
            <div className="grid grid-cols-2 gap-2">{drawButtons}</div>
            <div className="border-t border-hall-line pt-3">
              <p className="mb-3 font-sans text-[0.65rem] font-medium tracking-[0.28em] text-cream-muted uppercase">Settings</p>
              {settingsFields}
            </div>
            {popupBlocked ? (
              <a href={STAGE_PATH} target={STAGE_WINDOW} className="rounded-2xl border border-hall-line bg-hall-elevated px-3 py-2 text-center text-xs text-cream-muted">
                Popup blocked — open player view
              </a>
            ) : (
              <p className="text-center text-[0.7rem] leading-snug text-cream-muted">
                Drag the other window onto the hall display and press F to fullscreen. This side stays for Draw and Bingo.
              </p>
            )}
          </aside>
          <div className={`relative z-[2] min-h-0 min-w-0 flex-1 overflow-hidden px-1.5 py-1 md:px-3 md:py-2 ${canTapDraw || canTapSkip ? "cursor-pointer" : ""}`} onPointerUp={onSurfacePointer}>
            <div className="relative h-full min-h-0 w-full">
              <BoardGrid />
              {phase === "calling" && current != null ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-1/4 z-10 flex justify-center px-4">
                  <div className="v2-callout flex flex-col items-center">
                    <p className="mb-2 font-sans text-[0.7rem] tracking-[0.32em] text-cream-muted uppercase">Now calling</p>
                    <div className="flex items-center gap-3 rounded-full border border-hall-line bg-hall-panel/90 px-7 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.45)] md:gap-5 md:px-10 md:py-4">
                      <span className="font-display text-6xl font-semibold leading-none tracking-tight text-cream md:text-8xl">{letter}</span>
                      <span className="font-display text-6xl font-semibold tabular-nums leading-none tracking-tight text-cream md:text-8xl">{current}</span>
                    </div>
                  </div>
                </div>
              ) : null}
              {phase === "celebrating" ? (
                <p className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center font-display text-[12vw] leading-none font-semibold tracking-tight text-cream drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)] md:text-7xl">Bingo</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <>
          <header className="relative z-30 flex shrink-0 items-center gap-2 px-3 pt-[max(0.35rem,env(safe-area-inset-top))] pb-1 md:gap-3 md:px-6 md:pt-[max(0.75rem,env(safe-area-inset-top))] md:pb-2">
            <div className="min-w-0 flex-1">
              <p className="hidden font-sans text-[0.65rem] font-medium tracking-[0.28em] text-cream-muted uppercase md:block">Hall Caller</p>
              <h1 className="v2-title font-display text-[1.15rem] leading-none font-semibold tracking-tight md:text-3xl">Bingo Blast</h1>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 md:gap-2">
              {last != null && phase !== "calling" ? (
                <div className="hidden rounded-full border border-hall-line bg-hall-elevated px-3 py-1.5 md:block">
                  <span className="mr-1.5 text-[0.65rem] tracking-[0.18em] text-cream-muted uppercase">Last</span>
                  <span className="font-display text-lg font-semibold tabular-nums">{lastLetter}{last}</span>
                </div>
              ) : null}
              <div className="flex items-center rounded-full border border-hall-line bg-hall-elevated px-2 py-1 font-sans text-xs tabular-nums text-cream-muted md:px-3 md:py-1.5 md:text-sm">
                {last != null && phase !== "calling" ? (
                  <span className="mr-1.5 font-display text-sm font-semibold text-cream md:hidden">{lastLetter}{last}</span>
                ) : null}
                {called.length}<span className="text-cream-muted/60">/75</span>
              </div>
              {chromeButtons}
            </div>
          </header>

          <div className="relative z-[2] min-h-0 flex-1 basis-0 overflow-hidden px-1.5 md:px-4 md:pb-2">
            <HallBoard sceneOn={sceneOn} onSceneFail={() => setSceneFailed(true)} />
          </div>

          <div className="relative z-30 grid shrink-0 grid-cols-2 gap-2 px-3 pt-1 pb-[max(0.45rem,env(safe-area-inset-bottom))] md:mx-auto md:w-full md:max-w-xl md:gap-3 md:px-4 md:pb-[max(0.85rem,env(safe-area-inset-bottom))]">
            {drawButtons}
          </div>
        </>
      )}

      <div className="sr-only" aria-live="assertive">{phase === "calling" && current != null ? callLabel(current) : ""}</div>

      {phase === "celebrating" && !split && (
        <div data-no-draw className="absolute inset-0 z-30 bg-hall/50" onClick={endCelebrate} />
      )}

      {settingsOpen && !split && (
        <div data-no-draw className="absolute inset-0 z-40 flex items-end justify-center bg-hall/70 px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] md:items-center md:p-4" onClick={() => { setSettingsOpen(false); setConfirmReset(false); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="v2-settings-title" className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-[1.75rem] border border-hall-line bg-hall-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative z-10 flex shrink-0 items-center justify-between gap-3 border-b border-hall-line bg-hall-panel px-5 py-3">
              <h2 id="v2-settings-title" className="font-display text-2xl font-semibold">Settings</h2>
              <button type="button" onClick={() => { setSettingsOpen(false); setConfirmReset(false); }} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-hall-line bg-hall-elevated" aria-label="Close settings"><X className="h-5 w-5" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {settingsFields}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
